import { memo, useMemo } from 'react';
import classNames from 'classnames';
import { Badge, Icon, Text, useTheme } from '@Sage/hoi-poi-ui';

import { getLiteral, getLiteralPluralized } from 'utils/getLiteral';
import {
    formatMarketingCampaignMetricLabel,
    getMarketingCampaignCardMetrics,
    getMarketingCampaignRecipientsBreakdown,
} from '../../utils/marketingCampaignActivity';

import './MarketingCampaignActivityContent.scss';

const MarketingCampaignActivityContent = memo(
    ({ campaign, recipientsRightContent, variant = 'card' }) => {
        const theme = useTheme();
        const showPerformance = variant !== 'detail';

        const recipients = useMemo(
            () => getMarketingCampaignRecipientsBreakdown(campaign?.Recipients),
            [campaign?.Recipients],
        );

        const metrics = useMemo(
            () => (showPerformance ? getMarketingCampaignCardMetrics(campaign?.GlobalMetrics) : []),
            [campaign?.GlobalMetrics, showPerformance],
        );

        if (!campaign) return null;

        const recipientsBreakdown = recipients?.breakdown?.map(({ text }) => text).join(', ');

        return (
            <div
                className={classNames(
                    'marketing-campaign-card',
                    `marketing-campaign-card--${variant}`,
                )}
            >
                <Badge type="ongoing" className="marketing-campaign-card__tag" isActive>
                    {getLiteral('label_marketing')}
                </Badge>

                {recipients?.total !== undefined && recipients?.total !== null && (
                    <div className="marketing-campaign-card__recipients-row">
                        <div className="marketing-campaign-card__recipients">
                            <Text color={theme.colors.utility.textSecondary} tag="span">
                                {getLiteral('label_to')}:
                            </Text>
                            <Text
                                tag="span"
                                color={theme.colors.utility.textSecondary}
                                className="marketing-campaign-card__recipients-values"
                            >
                                <span className="marketing-campaign-card__recipients-total">
                                    {getLiteralPluralized(
                                        recipients.total,
                                        'label_one_recipient',
                                        'label_n_recipients',
                                    )}
                                </span>
                                {recipientsBreakdown && ` - ${recipientsBreakdown}`}
                            </Text>
                        </div>
                        {recipientsRightContent && (
                            <div className="marketing-campaign-card__recipients-right">
                                {recipientsRightContent}
                            </div>
                        )}
                    </div>
                )}

                {showPerformance && (campaign.Subject || metrics.length > 0) && (
                    <div className="marketing-campaign-card__performance">
                        {campaign.Subject && (
                            <Text type="body" className="marketing-campaign-card__subject">
                                {campaign.Subject}
                            </Text>
                        )}

                        {metrics.length > 0 && (
                            <div className="marketing-campaign-card__metrics">
                                {metrics.map(({ icon, value, labelKey }) => (
                                    <div key={labelKey} className="marketing-campaign-card__metric">
                                        <Icon
                                            name={icon}
                                            size="medium"
                                            color={theme.colors.grey[500]}
                                        />
                                        <Text
                                            type="body"
                                            color={theme.colors.utility.textSecondary}
                                        >
                                            {formatMarketingCampaignMetricLabel(value, labelKey)}
                                        </Text>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    },
);

MarketingCampaignActivityContent.displayName = 'MarketingCampaignActivityContent';

export default MarketingCampaignActivityContent;
